import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';

export function QrPrintPage() {
  const { id } = useParams();
  const stationId = Number(id);
  const navigate = useNavigate();
  const t = useT();
  const station = useQuery({
    queryKey: queryKeys.stations.detail(stationId),
    queryFn: () => eventsApi.getStation(stationId),
  });

  if (station.isLoading) return <div className="page"><Skeleton className="h-40" /></div>;
  if (!station.data || !station.data.surveysId)
    return (
      <p className="page text-sm text-[var(--color-fg-muted)]">
        {t('stations.qrPrint.noSurvey')}
      </p>
    );

  const publicBase = import.meta.env.VITE_PUBLIC_URL ?? `${window.location.origin}/public-surveys`;
  // When the station belongs to a shared-QR group, the printed QR must be the
  // SHARED one (`/sg/:groupId`) — scanning it asks the consumer to pick their
  // station first. Otherwise it's the station's own direct QR.
  const shared = !!station.data.qrGroupId;
  const longUrl = shared
    ? `${window.location.origin}/sg/${station.data.qrGroupId}`
    : `${publicBase}?surveyId=${station.data.surveysId}&stationId=${station.data.id}`;
  const shortUrl = shared
    ? `${window.location.origin}/sg/${station.data.qrGroupId}`
    : `${window.location.origin}/s/${station.data.surveysId}/${station.data.id}`;

  const downloadSvg = () => {
    const svg = document.getElementById('xotski-qr')?.outerHTML;
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `xotski-station-${station.data.id}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page space-y-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('stations.qrPrint.back')}
      </button>

      <Card>
        <CardHeader>
          <CardTitle>{t('stations.qrPrint.title')} · {station.data.name}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-8 md:grid-cols-[auto_1fr]">
          <div className="grid h-72 w-72 place-items-center rounded-2xl bg-white p-6">
            <QRCodeSVG id="xotski-qr" value={shortUrl} size={240} level="H" />
          </div>
          <div className="space-y-4">
            {shared && (
              <div className="rounded-lg border border-[var(--color-accent)] bg-[var(--color-accent)]/10 px-3 py-2 text-xs text-[var(--color-fg-primary)]">
                {t('stations.qrPrint.sharedNote')}
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t('stations.qrPrint.shortUrl')}
              </p>
              <p className="break-all font-mono text-sm">{shortUrl}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t('stations.qrPrint.fullUrl')}
              </p>
              <p className="break-all font-mono text-sm">{longUrl}</p>
            </div>
            <div className="flex gap-2">
              <Button leftIcon={<Download className="h-4 w-4" />} onClick={downloadSvg}>
                {t('stations.qrPrint.downloadSvg')}
              </Button>
              <Button
                variant="outline"
                leftIcon={<Printer className="h-4 w-4" />}
                onClick={() => window.print()}
              >
                {t('stations.qrPrint.print')}
              </Button>
              <a href={shortUrl} target="_blank" rel="noreferrer">
                <Button variant="ghost">{t('stations.qrPrint.openNewTab')}</Button>
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
